import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { AppID, TagsFilter, TagsFilterSerialized } from '@playq/octopus-common';
import { FilesFilterField, QueryIntelliTagResponse } from '@playq/octopus2-files';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';

import { IUseQueryOptionsExtended, useEitherQuery } from '/api/service-hooks';
import { services2 } from '/api/services2';
import { appToolkit } from '/store';
import { snackbarService } from '/common/snackbarService';

import { assetsQueryKeys } from './constants';

const offsetLimit = new OffsetLimit({ limit: 30, offset: 0 });

export const useAssetsIntelliTagsQuery = (
  tagsFilterSerialized: TagsFilterSerialized | undefined,
  options?: IUseQueryOptionsExtended<QueryIntelliTagResponse | undefined, GenericFailure | Error>
) => {
  const appID = useSelector(appToolkit.selectors.appID);

  const queryKeys = assetsQueryKeys.concat(appID, 'intelliTags', tagsFilterSerialized);

  const tagsFilter = useMemo(
    () => (tagsFilterSerialized === undefined ? new TagsFilter() : new TagsFilter(tagsFilterSerialized)),
    [tagsFilterSerialized]
  );

  const enabled = options?.enabled !== false && appID !== undefined;

  const { mutate: _mutate, ...res } = useEitherQuery(
    queryKeys,
    () =>
      services2.appAssetsService.queryIntelliTags(appID as AppID, offsetLimit, {
        [FilesFilterField.Tag]: tagsFilter,
      }),
    {
      staleTime: Infinity,
      meta: {
        onError: (e: GenericFailure) => {
          console.error(e);
          snackbarService.genericFailure(e);
        },
      },
      ...options,
      enabled,
    }
  );

  return {
    ...res,
    intelliTags: res.data,
    queryKeys,
  };
};
